import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  BuildingOfficeIcon, UserGroupIcon, AcademicCapIcon,
  BanknotesIcon, ArrowRightOnRectangleIcon, ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline';

const fmtMoney = n => `Rs. ${(n||0).toLocaleString('en-PK')}`;
const fmtShort = n => {
  n = n || 0;
  if (n >= 10000000) return `Rs.${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `Rs.${(n/100000).toFixed(1)}L`;
  if (n >= 1000)     return `Rs.${(n/1000).toFixed(1)}K`;
  return `Rs.${Math.round(n)}`;
};
const fmtNum = n => (n||0).toLocaleString('en-PK');
const getInitials = (name='') => name.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'S';

const PALETTE = {
  green:  '#10c98e', greenLight: '#ecfdf5', greenDark: '#0a9e70',
  red:    '#ef4444', redLight:   '#fef2f2',
  blue:   '#3b6ef8', blueLight:  '#eef2ff',
  purple: '#8b5cf6', purpleLight:'#f5f3ff',
  amber:  '#f59e0b', amberLight: '#fffbeb',
  teal:   '#0d9488', tealLight:  '#f0fdfa',
  indigo: '#6366f1', orange: '#f97316', pink: '#ec4899',
  cyan:   '#06b6d4', lime:   '#84cc16',
};

const EXPENSE_COLORS = [
  PALETTE.blue, PALETTE.green, PALETTE.amber, PALETTE.red,
  PALETTE.purple, PALETTE.cyan, PALETTE.lime, PALETTE.orange,
  PALETTE.pink, PALETTE.indigo, PALETTE.teal, '#a855f7',
];

// ── Modern grouped bar chart ──────────────────────────────────────────────────
const GroupedBar = ({ data=[], keys=[], colors=[], labels=[], height=200, yFmt=v=>v }) => {
  if (!data.length) return <Empty h={height}/>;
  const maxV  = Math.max(...data.flatMap(d => keys.map(k => d[k]||0)), 1);
  const W     = 560; const padL = 52; const padB = 36; const padT = 16; const padR = 12;
  const chartW = W - padL - padR;
  const chartH = height - padT - padB;
  const bw     = Math.max(8, (chartW / (data.length * keys.length + data.length * 0.6 + 1)));
  const gw     = keys.length * bw + (keys.length-1)*3;
  const step   = chartW / data.length;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(p => ({ v: p*maxV, y: padT + chartH - p*chartH }));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${height}`} style={{fontFamily:'Sora,sans-serif',overflow:'visible'}}>
      <defs>
        {keys.map((k,i) => (
          <linearGradient key={k} id={`gb-${k}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors[i]} stopOpacity="1"/>
            <stop offset="100%" stopColor={colors[i]} stopOpacity="0.6"/>
          </linearGradient>
        ))}
      </defs>
      {/* Grid lines */}
      {yTicks.map((t,i) => (
        <g key={i}>
          <line x1={padL} y1={t.y} x2={W-padR} y2={t.y} stroke="#f0f2f8" strokeWidth={i===4?1.5:1} strokeDasharray={i===4?'0':'4,4'}/>
          <text x={padL-6} y={t.y+4} textAnchor="end" fontSize={9} fill="#b0b5cc" fontWeight="500">{yFmt(Math.round(t.v))}</text>
        </g>
      ))}
      {/* Bars */}
      {data.map((d, di) => {
        const gx = padL + di * step + (step - gw) / 2;
        return (
          <g key={di}>
            {keys.map((k, ki) => {
              const val = d[k] || 0;
              const bh  = Math.max(3, (val/maxV) * chartH);
              const bx  = gx + ki*(bw+3);
              const by  = padT + chartH - bh;
              return (
                <g key={k}>
                  <rect x={bx} y={by} width={bw} height={bh} rx={4}
                    fill={`url(#gb-${k})`} />
                  {/* Value label on top */}
                  {bh > 20 && (
                    <text x={bx+bw/2} y={by-4} textAnchor="middle" fontSize={7.5} fill={colors[ki]} fontWeight="700">
                      {yFmt(val).replace('Rs.','').replace(/\.0/,'')}
                    </text>
                  )}
                </g>
              );
            })}
            {/* X label */}
            <text x={gx+gw/2} y={padT+chartH+16} textAnchor="middle" fontSize={9.5} fill="#6b7194" fontWeight="600">
              {String(d.label||d.name||'').slice(0,11)}
            </text>
            {String(d.label||d.name||'').length > 11 && (
              <text x={gx+gw/2} y={padT+chartH+26} textAnchor="middle" fontSize={8} fill="#9094a8">
                {String(d.label||d.name||'').slice(11,22)}
              </text>
            )}
          </g>
        );
      })}
      {/* Y axis line */}
      <line x1={padL} y1={padT} x2={padL} y2={padT+chartH} stroke="#e0e4f0" strokeWidth={1.5}/>
      {/* Legend */}
      {keys.map((k,i) => (
        <g key={k} transform={`translate(${padL + i*90}, ${height - 6})`}>
          <rect width={10} height={10} rx={3} fill={colors[i]} y={-10}/>
          <text x={14} y={0} fontSize={9} fill="#6b7194" fontWeight="600">{(labels[i]||k).slice(0,10)}</text>
        </g>
      ))}
    </svg>
  );
};

// ── Modern area/line chart ────────────────────────────────────────────────────
const AreaLine = ({ data=[], keys=[], colors=[], labels=[], height=180, yFmt=v=>v }) => {
  if (!data.length) return <Empty h={height}/>;
  const maxV  = Math.max(...data.flatMap(d => keys.map(k => d[k]||0)), 1);
  const W     = 560; const padL = 52; const padB = 30; const padT = 16; const padR = 12;
  const chartW = W - padL - padR;
  const chartH = height - padT - padB;
  const xs     = data.map((_,i) => padL + (i/(data.length-1||1))*chartW);
  const ys     = (k) => data.map(d => padT + chartH - ((d[k]||0)/maxV)*chartH);
  const yTicks = [0,0.25,0.5,0.75,1].map(p => ({ v:p*maxV, y: padT+chartH - p*chartH }));

  const polyline = (k) => xs.map((x,i) => `${x},${ys(k)[i]}`).join(' ');
  const area     = (k) => {
    const pts = xs.map((x,i) => `${x},${ys(k)[i]}`).join(' ');
    return `M ${padL},${padT+chartH} L ${pts.split(' ').map((p,i) => `${xs[i]},${ys(k)[i]}`).join(' ')} L ${xs[xs.length-1]},${padT+chartH} Z`;
  };

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${height}`} style={{fontFamily:'Sora,sans-serif',overflow:'visible'}}>
      <defs>
        {keys.map((k,i) => (
          <linearGradient key={k} id={`al-${k}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={colors[i]} stopOpacity="0.25"/>
            <stop offset="100%" stopColor={colors[i]} stopOpacity="0.02"/>
          </linearGradient>
        ))}
      </defs>
      {yTicks.map((t,i) => (
        <g key={i}>
          <line x1={padL} y1={t.y} x2={W-padR} y2={t.y} stroke="#f0f2f8" strokeWidth={i===4?1.5:1} strokeDasharray={i===4?'0':'4,4'}/>
          <text x={padL-6} y={t.y+4} textAnchor="end" fontSize={9} fill="#b0b5cc" fontWeight="500">{yFmt(Math.round(t.v))}</text>
        </g>
      ))}
      {keys.map((k,i) => (
        <g key={k}>
          {/* Area fill */}
          <path d={area(k)} fill={`url(#al-${k})`} />
          {/* Line */}
          <polyline points={polyline(k)} fill="none" stroke={colors[i]} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round"/>
          {/* Dots */}
          {xs.map((x,j) => (
            <g key={j}>
              <circle cx={x} cy={ys(k)[j]} r={4} fill="white" stroke={colors[i]} strokeWidth={2.5}/>
              {data.length <= 8 && (
                <text x={x} y={ys(k)[j]-9} textAnchor="middle" fontSize={8} fill={colors[i]} fontWeight="700">
                  {yFmt(data[j][k]||0).replace('Rs.','')}
                </text>
              )}
            </g>
          ))}
        </g>
      ))}
      {/* X labels */}
      {data.map((d,i) => (
        <text key={i} x={xs[i]} y={padT+chartH+16} textAnchor="middle" fontSize={9.5} fill="#6b7194" fontWeight="600">
          {String(d.label||'').slice(0,7)}
        </text>
      ))}
      <line x1={padL} y1={padT} x2={padL} y2={padT+chartH} stroke="#e0e4f0" strokeWidth={1.5}/>
      {keys.map((k,i) => (
        <g key={k} transform={`translate(${padL + i*100}, ${height-4})`}>
          <rect width={10} height={3} rx={2} fill={colors[i]} y={-3}/>
          <text x={14} y={1} fontSize={9} fill="#6b7194" fontWeight="600">{labels[i]||k}</text>
        </g>
      ))}
    </svg>
  );
};

// ── Horizontal bar chart (for branch comparison) ──────────────────────────────
const HorizBar = ({ data=[], valueKey='', color='#3b6ef8', fmt=v=>v, height=200 }) => {
  if (!data.length) return <Empty h={height}/>;
  const maxV = Math.max(...data.map(d => d[valueKey]||0), 1);
  const rowH = Math.min(36, (height-10) / data.length);
  return (
    <svg width="100%" viewBox={`0 0 560 ${data.length*rowH+10}`} style={{fontFamily:'Sora,sans-serif'}}>
      <defs>
        <linearGradient id={`hb-${valueKey}`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.9"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.4"/>
        </linearGradient>
      </defs>
      {data.map((d,i) => {
        const val = d[valueKey] || 0;
        const bw  = Math.max(4, (val/maxV)*340);
        const y   = i*rowH + 5;
        return (
          <g key={i}>
            <text x={0} y={y+rowH*0.62} fontSize={10} fill="#6b7194" fontWeight="600">
              {String(d.name||'').slice(0,14)}
            </text>
            <rect x={140} y={y+4} width={360} height={rowH-10} rx={4} fill="#f3f5fb"/>
            <rect x={140} y={y+4} width={bw} height={rowH-10} rx={4} fill={`url(#hb-${valueKey})`}/>
            <text x={140+bw+6} y={y+rowH*0.62} fontSize={10} fill={color} fontWeight="700">
              {fmt(val)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

// ── Donut chart ───────────────────────────────────────────────────────────────
const Donut = ({ data=[], colors=[], size=150, label='' }) => {
  if (!data.length) return <Empty h={size}/>;
  const total = data.reduce((s,d) => s+(d.total||0), 0) || 1;
  const r=52; const ir=30; const cx=size/2; const cy=size/2;
  let angle = -Math.PI/2;
  const slices = data.slice(0,10).map((d,i) => {
    const pct  = (d.total||0)/total;
    const sa   = angle;
    const ea   = angle + pct*2*Math.PI - 0.02;
    angle      = angle + pct*2*Math.PI;
    const x1=cx+r*Math.cos(sa); const y1=cy+r*Math.sin(sa);
    const x2=cx+r*Math.cos(ea); const y2=cy+r*Math.sin(ea);
    const xi1=cx+ir*Math.cos(sa); const yi1=cy+ir*Math.sin(sa);
    const xi2=cx+ir*Math.cos(ea); const yi2=cy+ir*Math.sin(ea);
    const lg = pct > 0.5 ? 1 : 0;
    return {
      path:`M ${xi1} ${yi1} L ${x1} ${y1} A ${r} ${r} 0 ${lg} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${ir} ${ir} 0 ${lg} 0 ${xi1} ${yi1} Z`,
      color: colors[i%colors.length], pct, label: d.category||d.name||''
    };
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{fontFamily:'Sora,sans-serif'}}>
      <defs>
        {slices.map((s,i) => (
          <radialGradient key={i} id={`dg-${i}`}>
            <stop offset="0%" stopColor={s.color} stopOpacity="0.9"/>
            <stop offset="100%" stopColor={s.color} stopOpacity="0.7"/>
          </radialGradient>
        ))}
      </defs>
      {slices.map((s,i) => (
        <path key={i} d={s.path} fill={`url(#dg-${i})`} stroke="white" strokeWidth={2}/>
      ))}
      <circle cx={cx} cy={cy} r={ir-1} fill="white"/>
      <text x={cx} y={cy-5} textAnchor="middle" fontSize={8} fill="#9094a8" fontWeight="600" textLength={40} lengthAdjust="spacing">{label||'Total'}</text>
      <text x={cx} y={cy+8} textAnchor="middle" fontSize={11} fill="#0f1123" fontWeight="700">{fmtShort(total)}</text>
    </svg>
  );
};

const Empty = ({ h=160 }) => (
  <div style={{height:h,display:'flex',alignItems:'center',justifyContent:'center',color:'#c0c5dc',fontSize:'.76rem',flexDirection:'column',gap:6}}>
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 9h18M9 21V9"/></svg>
    No data yet
  </div>
);

// ── CSS ───────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .od * { box-sizing:border-box; }
  .od { font-family:'Sora',sans-serif; color:#1a1d2e; animation:odFade .5s ease both; }
  @keyframes odFade { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  .od-greeting { font-size:1.55rem; font-weight:700; color:#0f1123; letter-spacing:-.025em; margin-bottom:3px; }
  .od-sub { font-size:.8rem; color:#9094a8; margin-bottom:2rem; }

  /* ── Stat cards ── */
  .od-stats { display:grid; grid-template-columns:repeat(6,1fr); gap:.9rem; margin-bottom:1.75rem; }
  .od-stat { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; position:relative; overflow:hidden; transition:transform .18s,box-shadow .18s; }
  .od-stat:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(0,0,0,.06); }
  .od-stat::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; border-radius:16px 16px 0 0; }
  .od-stat.green::before{background:linear-gradient(90deg,#10c98e,#06b6d4)}
  .od-stat.blue::before{background:linear-gradient(90deg,#3b6ef8,#8b5cf6)}
  .od-stat.purple::before{background:linear-gradient(90deg,#8b5cf6,#ec4899)}
  .od-stat.amber::before{background:linear-gradient(90deg,#f59e0b,#f97316)}
  .od-stat.red::before{background:linear-gradient(90deg,#ef4444,#f97316)}
  .od-stat.teal::before{background:linear-gradient(90deg,#0d9488,#10c98e)}
  .od-si { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .od-si svg { width:16px; height:16px; stroke-width:1.8; }
  .od-si.green{background:#ecfdf5;color:#10c98e} .od-si.blue{background:#eef2ff;color:#3b6ef8}
  .od-si.purple{background:#f5f3ff;color:#8b5cf6} .od-si.amber{background:#fffbeb;color:#f59e0b}
  .od-si.red{background:#fef2f2;color:#ef4444} .od-si.teal{background:#f0fdfa;color:#0d9488}
  .od-sl { font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; }
  .od-sv { font-size:1.3rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.2; margin-top:2px; }
  .od-ss { font-size:.63rem; color:#b0b5cc; margin-top:4px; display:flex; align-items:center; gap:4px; flex-wrap:wrap; }
  .up   { color:#10c98e; font-weight:700; } .dn { color:#ef4444; font-weight:700; }

  /* ── Layout ── */
  .od-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .od-grid-3 { display:grid; grid-template-columns:1fr 1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .od-full { grid-column:1/-1; }
  .od-card { background:white; border-radius:18px; border:1px solid #e8ebf4; padding:1.3rem 1.5rem; transition:box-shadow .18s; }
  .od-card:hover { box-shadow:0 4px 18px rgba(0,0,0,.05); }
  .od-ct { font-size:.9rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .od-cs { font-size:.7rem; color:#9094a8; margin-bottom:1.1rem; }

  /* ── Expense legend ── */
  .exp-wrap { display:flex; gap:16px; align-items:flex-start; }
  .exp-legend { flex:1; min-width:0; display:flex; flex-direction:column; gap:5px; }
  .exp-row { display:flex; align-items:center; gap:7px; font-size:.74rem; }
  .exp-dot { width:9px; height:9px; border-radius:3px; flex-shrink:0; }
  .exp-cat { color:#6b7194; flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; text-transform:capitalize; font-weight:500; }
  .exp-pct { font-size:.63rem; color:#b0b5cc; white-space:nowrap; }
  .exp-amt { font-family:'JetBrains Mono',monospace; font-weight:700; color:#0f1123; font-size:.72rem; white-space:nowrap; }

  /* ── Fee table ── */
  .ft { width:100%;min-width:0; border-collapse:collapse; }
  .ft th { padding:8px 12px; text-align:center !important; font-size:.62rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; border-bottom:1.5px solid #f0f2f8; }
  .ft td { padding:9px 12px; font-size:.78rem; border-bottom:1px solid #f3f5fb; text-align:center !important; }
  .ft tr:last-child td { border-bottom:none; }
  .ft tr:hover td { background:#fafbff; }
  .prog { height:5px; border-radius:3px; background:#f3f5fb; overflow:hidden; margin-top:3px; }
  .prog-fill { height:100%; border-radius:3px; }
  .od-ft-cards { display:none; }
  .od-ft-card  { background:white; border:1px solid #e8ebf4; border-radius:12px; padding:13px; margin-bottom:8px; }
  .od-ft-row  { display:flex; justify-content:space-between; align-items:center; padding:4px 0; gap:10px; }
  .od-ft-row:not(:last-child) { border-bottom:1px solid #f3f5fb; }
  .od-ft-label { font-size:.63rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; flex-shrink:0; }

  /* ── Branch list ── */
  .od-br-title { font-size:1rem; font-weight:700; color:#0f1123; margin:1.5rem 0 .9rem; }
  .od-br-list { display:flex; flex-direction:column; gap:.7rem; }
  .od-br { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.2rem; display:flex; align-items:center; gap:14px; cursor:pointer; transition:all .15s; }
  .od-br:hover { border-color:#c4b5fd; box-shadow:0 4px 16px rgba(124,58,237,.09); transform:translateY(-1px); }
  .od-br-logo { width:42px; height:42px; border-radius:12px; background:linear-gradient(135deg,#7c3aed,#4f46e5); display:flex; align-items:center; justify-content:center; font-weight:700; color:white; font-size:.9rem; flex-shrink:0; overflow:hidden; }
  .od-br-logo img { width:100%; height:100%; object-fit:cover; }
  .od-br-name { font-weight:700; font-size:.9rem; color:#0f1123; }
  .od-br-meta { font-size:.7rem; color:#9094a8; margin-top:2px; }
  .od-br-stats { display:flex; gap:14px; }
  .od-bs { text-align:right; }
  .od-bs-l { font-size:.6rem; color:#9094a8; font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
  .od-bs-v { font-size:.85rem; font-weight:700; font-family:'JetBrains Mono',monospace; }
  .od-bs-v.g{color:#10c98e} .od-bs-v.r{color:#ef4444} .od-bs-v.b{color:#3b6ef8} .od-bs-v.p{color:#8b5cf6}
  .od-enter { display:flex; align-items:center; gap:5px; font-size:.76rem; font-weight:700; color:#7c3aed; background:#f5f3ff; padding:6px 14px; border-radius:9px; border:none; cursor:pointer; font-family:'Sora',sans-serif; flex-shrink:0; transition:all .15s; }
  .od-enter:hover { background:#ede9fe; box-shadow:0 2px 8px rgba(124,58,237,.2); }
  .od-enter svg { width:14px; height:14px; stroke-width:2.5; }

  .od-loading { display:flex; align-items:center; justify-content:center; height:220px; gap:10px; color:#9094a8; font-size:.86rem; }
  .od-spin { width:20px; height:20px; border:2.5px solid #e4e7f0; border-top-color:#7c3aed; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }

  @media (max-width:1280px) { .od-stats{grid-template-columns:repeat(3,1fr);} .od-grid-3{grid-template-columns:1fr 1fr;} }
  @media (max-width:900px)  { .od-grid-2,.od-grid-3{grid-template-columns:1fr;} .od-full{grid-column:1;} }
  @media (max-width:768px)  { .od-ft-table-wrap{display:none !important;} .od-ft-cards{display:block;} }
  @media (max-width:640px)  { .od-stats{grid-template-columns:1fr 1fr;} .od-br-stats{display:none;} }
`;

export default function OwnerDashboard() {
  const { user, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [data,     setData]     = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [entering, setEntering] = useState(null);

  useEffect(() => {
    Promise.all([api.get('/owner/stats'), api.get('/owner/schools')])
      .then(([s, b]) => {
        setData(s.data);
        setBranches(b.data?.schools || b.data || []);
      })
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const handleEnter = async (branch) => {
    setEntering(branch._id);
    try {
      const { data: res } = await api.post(`/owner/enter/${branch._id}`);
      localStorage.setItem('ownerToken', localStorage.getItem('token') || '');
      await loginWithToken(res.token, res.user);
      navigate('/dashboard');
      toast.success(`Entered ${branch.name} as Admin`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
      setEntering(null);
    }
  };

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';

  const totals   = data?.totals             || {};
  const rvex     = data?.revenueVsExpenses  || [];
  const feeTrend = data?.feeCollectionChart || [];
  const expCat   = data?.expenseByCategory  || [];
  const branchBD = data?.branchBreakdown    || [];

  const enriched = branches.map(b => ({
    ...b,
    ...(branchBD.find(d => d.schoolId?.toString() === b._id?.toString()) || {}),
  }));

  const totalExpCat = expCat.reduce((s,e) => s+(e.total||0), 0) || 1;

  const stuTrend = totals.newStudentsLastMonth > 0
    ? Math.round(((totals.newStudentsThisMonth||0) - totals.newStudentsLastMonth) / totals.newStudentsLastMonth * 100) : null;
  const teaTrend = totals.newTeachersLastMonth > 0
    ? Math.round(((totals.newTeachersThisMonth||0) - totals.newTeachersLastMonth) / totals.newTeachersLastMonth * 100) : null;

  // Branch data for charts
  const branchStudents  = enriched.map(b => ({ name: b.name, students:  b.students||0,  label: b.name }));
  const branchTeachers  = enriched.map(b => ({ name: b.name, teachers:  b.teachers||0,  label: b.name }));
  const branchRevEx     = enriched.map(b => ({ name: b.name, revenue: Math.round(b.revenue||0), expenses: Math.round(b.expenses||0), label: b.name }));

  return (
    <div className="od"><style>{css}</style>

      <div className="od-greeting">{greeting}, {user?.name?.split(' ')[0]||'Owner'} 👋</div>
      <div className="od-sub">Complete overview across all your school branches.</div>

      {loading ? (
        <div className="od-loading"><div className="od-spin"/>Loading dashboard…</div>
      ) : (<>

        {/* ── Stat cards ── */}
        <div className="od-stats">
          <div className="od-stat purple">
            <div className="od-si purple"><BuildingOfficeIcon/></div>
            <div className="od-sl">Branches</div>
            <div className="od-sv">{fmtNum(totals.schools)}</div>
            <div className="od-ss">active schools</div>
          </div>
          <div className="od-stat blue">
            <div className="od-si blue"><UserGroupIcon/></div>
            <div className="od-sl">Students</div>
            <div className="od-sv">{fmtNum(totals.totalStudents)}</div>
            <div className="od-ss">
              {stuTrend !== null && <span className={stuTrend>=0?'up':'dn'}>{stuTrend>=0?'▲':'▼'}{Math.abs(stuTrend)}%</span>}
              <span>+{fmtNum(totals.newStudentsThisMonth)} this month</span>
            </div>
          </div>
          <div className="od-stat green">
            <div className="od-si green"><AcademicCapIcon/></div>
            <div className="od-sl">Teachers</div>
            <div className="od-sv">{fmtNum(totals.totalTeachers)}</div>
            <div className="od-ss">
              {teaTrend !== null && <span className={teaTrend>=0?'up':'dn'}>{teaTrend>=0?'▲':'▼'}{Math.abs(teaTrend)}%</span>}
              <span>+{fmtNum(totals.newTeachersThisMonth)} this month</span>
            </div>
          </div>
          <div className="od-stat amber">
            <div className="od-si amber"><BanknotesIcon/></div>
            <div className="od-sl">Revenue</div>
            <div className="od-sv" style={{fontSize:'1rem'}}>{fmtShort(totals.totalRevenue)}</div>
            <div className="od-ss">total fees collected</div>
          </div>
          <div className="od-stat red">
            <div className="od-si red"><ArrowTrendingDownIcon/></div>
            <div className="od-sl">Expenses</div>
            <div className="od-sv" style={{fontSize:'1rem'}}>{fmtShort(totals.totalExpenses)}</div>
            <div className="od-ss">total spending</div>
          </div>
          <div className={`od-stat ${(totals.netProfit||0)>=0?'teal':'red'}`}>
            <div className={`od-si ${(totals.netProfit||0)>=0?'teal':'red'}`}><ArrowTrendingUpIcon/></div>
            <div className="od-sl">Net Profit</div>
            <div className="od-sv" style={{fontSize:'1rem',color:(totals.netProfit||0)>=0?'#0d9488':'#ef4444'}}>
              {fmtShort(Math.abs(totals.netProfit||0))}
            </div>
            <div className="od-ss">{(totals.netProfit||0)>=0?'surplus':'deficit'}</div>
          </div>
        </div>

        {/* ── Row 1: Revenue vs Expenses trend + Expense donut ── */}
        <div className="od-grid-2" style={{marginBottom:'1rem'}}>
          <div className="od-card">
            <div className="od-ct">Revenue vs Expenses — Monthly Trend</div>
            <div className="od-cs">Last 6 months across all branches</div>
            <AreaLine
              data={feeTrend}
              keys={['revenue','expenses']}
              colors={[PALETTE.green, PALETTE.red]}
              labels={['Revenue','Expenses']}
              height={185}
              yFmt={fmtShort}
            />
          </div>
          <div className="od-card">
            <div className="od-ct">Expense Breakdown</div>
            <div className="od-cs">By category — last 6 months</div>
            {expCat.length === 0 ? <Empty h={185}/> : (
              <div className="exp-wrap">
                <Donut data={expCat} colors={EXPENSE_COLORS} size={150} label="Expenses"/>
                <div className="exp-legend">
                  {expCat.slice(0,9).map((e,i) => (
                    <div key={e.category} className="exp-row">
                      <div className="exp-dot" style={{background:EXPENSE_COLORS[i%EXPENSE_COLORS.length]}}/>
                      <span className="exp-cat">{e.category}</span>
                      <span className="exp-pct">{Math.round((e.total||0)/totalExpCat*100)}%</span>
                      <span className="exp-amt">{fmtShort(e.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Row 2: Revenue vs Expenses per branch (grouped bar) ── */}
        <div className="od-card" style={{marginBottom:'1rem'}}>
          <div className="od-ct">Revenue vs Expenses — Per Branch</div>
          <div className="od-cs">All-time fee collection vs total spending per school</div>
          <GroupedBar
            data={branchRevEx}
            keys={['revenue','expenses']}
            colors={[PALETTE.green, PALETTE.red]}
            labels={['Revenue','Expenses']}
            height={200}
            yFmt={fmtShort}
          />
        </div>

        {/* ── Row 3: Students per branch + Teachers per branch ── */}
        <div className="od-grid-2" style={{marginBottom:'1rem'}}>
          <div className="od-card">
            <div className="od-ct">Students per Branch</div>
            <div className="od-cs">Total enrolled students by school</div>
            <HorizBar
              data={branchStudents}
              valueKey="students"
              color={PALETTE.blue}
              fmt={fmtNum}
              height={Math.max(120, enriched.length * 40)}
            />
          </div>
          <div className="od-card">
            <div className="od-ct">Teachers per Branch</div>
            <div className="od-cs">Active teaching staff by school</div>
            <HorizBar
              data={branchTeachers}
              valueKey="teachers"
              color={PALETTE.purple}
              fmt={fmtNum}
              height={Math.max(120, enriched.length * 40)}
            />
          </div>
        </div>

        {/* ── Row 4: Fee collection table ── */}
        <div className="od-card" style={{marginBottom:'1rem'}}>
          <div className="od-ct">Monthly Fee Collection Summary</div>
          <div className="od-cs">Revenue, expenses and net profit — last 6 months</div>
          <div className="od-ft-table-wrap" style={{overflowX:'auto'}}>
            <table className="ft">
              <thead><tr>
                <th>Month</th><th>Revenue</th><th>Expenses</th><th>Net</th><th>Bills Paid</th>
              </tr></thead>
              <tbody>
                {feeTrend.map(row => {
                  const net    = (row.revenue||0) - (row.expenses||0);
                  const maxRev = Math.max(...feeTrend.map(r => r.revenue||0), 1);
                  return (
                    <tr key={row.month}>
                      <td style={{fontWeight:700,color:'#0f1123'}}>{row.label}</td>
                      <td>
                        <div style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:PALETTE.green,fontSize:'.8rem'}}>{fmtMoney(row.revenue||0)}</div>
                        <div className="prog"><div className="prog-fill" style={{width:`${((row.revenue||0)/maxRev)*100}%`,background:PALETTE.green}}/></div>
                      </td>
                      <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,color:PALETTE.red,fontSize:'.8rem'}}>{fmtMoney(row.expenses||0)}</td>
                      <td style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,fontSize:'.82rem',color:net>=0?PALETTE.teal:PALETTE.red}}>
                        {net>=0?'+':''}{fmtMoney(net)}
                      </td>
                      <td style={{color:'#6b7194',fontSize:'.76rem'}}>{row.count||0} bills</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="od-ft-cards">
            {feeTrend.map(row => {
              const net    = (row.revenue||0) - (row.expenses||0);
              const maxRev = Math.max(...feeTrend.map(r => r.revenue||0), 1);
              return (
                <div key={row.month} className="od-ft-card">
                  <div className="od-ft-row">
                    <span className="od-ft-label">Month</span>
                    <span style={{fontWeight:700,color:'#0f1123',fontSize:'.82rem'}}>{row.label}</span>
                  </div>
                  <div className="od-ft-row">
                    <span className="od-ft-label">Revenue</span>
                    <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,color:PALETTE.green,fontSize:'.8rem'}}>{fmtMoney(row.revenue||0)}</span>
                  </div>
                  <div className="prog" style={{margin:'2px 0 8px'}}><div className="prog-fill" style={{width:`${((row.revenue||0)/maxRev)*100}%`,background:PALETTE.green}}/></div>
                  <div className="od-ft-row">
                    <span className="od-ft-label">Expenses</span>
                    <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,color:PALETTE.red,fontSize:'.8rem'}}>{fmtMoney(row.expenses||0)}</span>
                  </div>
                  <div className="od-ft-row">
                    <span className="od-ft-label">Net</span>
                    <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:700,fontSize:'.82rem',color:net>=0?PALETTE.teal:PALETTE.red}}>{net>=0?'+':''}{fmtMoney(net)}</span>
                  </div>
                  <div className="od-ft-row">
                    <span className="od-ft-label">Bills Paid</span>
                    <span style={{color:'#6b7194',fontSize:'.76rem'}}>{row.count||0} bills</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Branch list ── */}
        <div className="od-br-title">Your Branches</div>
        <div className="od-br-list">
          {enriched.map(b => (
            <div key={b._id} className="od-br" onClick={() => handleEnter(b)}>
              <div className="od-br-logo">
                {b.logo ? <img src={b.logo} alt={b.name}/> : getInitials(b.name)}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div className="od-br-name">{b.name}</div>
                <div className="od-br-meta">{fmtNum(b.students||0)} students · {fmtNum(b.teachers||0)} teachers{b.address?` · ${b.address}`:''}</div>
              </div>
              <div className="od-br-stats">
                <div className="od-bs"><div className="od-bs-l">Revenue</div><div className="od-bs-v g">{fmtShort(b.revenue||0)}</div></div>
                <div className="od-bs"><div className="od-bs-l">Expenses</div><div className="od-bs-v r">{fmtShort(b.expenses||0)}</div></div>
                <div className="od-bs"><div className="od-bs-l">Net</div><div className={`od-bs-v ${(b.profit||0)>=0?'g':'r'}`}>{fmtShort(Math.abs(b.profit||0))}</div></div>
                <div className="od-bs"><div className="od-bs-l">Students</div><div className="od-bs-v b">{fmtNum(b.students||0)}</div></div>
                <div className="od-bs"><div className="od-bs-l">Teachers</div><div className="od-bs-v p">{fmtNum(b.teachers||0)}</div></div>
              </div>
              <button className="od-enter" disabled={!!entering}
                onClick={e => { e.stopPropagation(); handleEnter(b); }}>
                {entering === b._id ? '…' : <><ArrowRightOnRectangleIcon />Enter</>}
              </button>
            </div>
          ))}
          {enriched.length === 0 && (
            <div style={{background:'white',borderRadius:14,border:'1px solid #e8ebf4',padding:'2.5rem',textAlign:'center',color:'#9094a8',fontSize:'.82rem'}}>
              No branches yet.{' '}
              <button onClick={() => navigate('/owner/add-branch')}
                style={{color:'#7c3aed',background:'none',border:'none',cursor:'pointer',fontWeight:700,fontFamily:'Sora',fontSize:'.82rem'}}>
                Add your first branch →
              </button>
            </div>
          )}
        </div>

      </>)}
    </div>
  );
}